/**
 * @fileoverview World Species V3 schema definitions for creatures, races, and flora.
 *
 * This file contains:
 * - Canonical field definitions for world species (single source of truth)
 * - Dynamically generated Zod schemas with type-safe casting
 * - All species-related schemas (entity, snapshot, idea, context variants)
 *
 * Pattern follows the character domain (character-sub-entities-v2.ts) and world-event.ts.
 */

import { z } from 'zod';
import { SHARED_WORLD_ELEMENT_FIELDS, type WorldElementFieldDefinition } from './shared-fields';

// =============================================================================
// CANONICAL FIELD DEFINITIONS - SINGLE SOURCE OF TRUTH
// =============================================================================

/**
 * Species-specific field definitions.
 * Combined with shared fields, these define all attributes of a world species.
 */
const SPECIES_SPECIFIC_FIELDS: WorldElementFieldDefinition[] = [
  {
    key: 'biology',
    label: 'Biology & Physiology',
    schemaDescription: "Physical characteristics and anatomy",
    initialStateDescription: "The physical characteristics, anatomy, and biological traits of this species.",
    journeyDescription: "Current understanding of the species' biology and any newly revealed physical traits.",
  },
  {
    key: 'habitat',
    label: 'Habitat & Range',
    schemaDescription: "Where they live and environmental needs",
    initialStateDescription: "Where this species lives, their environmental needs, and geographic range.",
    journeyDescription: "Known habitats and any changes to range or environmental conditions.",
  },
  {
    key: 'behavior',
    label: 'Behavior & Temperament',
    schemaDescription: "Typical actions and disposition",
    initialStateDescription: "The typical behavior patterns and temperament of this species.",
    journeyDescription: "Observed behaviors and any newly revealed behavioral traits or changes.",
  },
  {
    key: 'diet',
    label: 'Diet & Sustenance',
    schemaDescription: "What they eat or how they sustain themselves",
    initialStateDescription: "What this species eats or how they sustain themselves.",
    journeyDescription: "Known dietary habits and any newly revealed sustenance requirements.",
  },
  {
    key: 'intelligence',
    label: 'Intelligence & Communication',
    schemaDescription: "Cognitive abilities and how they communicate",
    initialStateDescription: "The cognitive abilities and communication methods of this species.",
    journeyDescription: "Current understanding of intelligence level and any newly revealed communication abilities.",
  },
  {
    key: 'society',
    label: 'Society & Culture',
    schemaDescription: "Social structures (if sentient)",
    initialStateDescription: "The social structures, cultural practices, and societal organization of this species (if sentient).",
    journeyDescription: "Known social structures and any newly revealed cultural practices or changes.",
  },
  {
    key: 'abilities',
    label: 'Abilities & Powers',
    schemaDescription: "Special capabilities or adaptations",
    initialStateDescription: "Special capabilities, powers, or unique adaptations of this species.",
    journeyDescription: "Known abilities and any newly discovered powers or adaptations.",
  },
  {
    key: 'population',
    label: 'Population & Rarity',
    schemaDescription: "Numbers and how common they are",
    initialStateDescription: "The population size and how common or rare this species is in the world.",
    journeyDescription: "Current population status and any changes to numbers or rarity.",
  },
];

/**
 * Complete field definitions for world species.
 * Combines shared fields with species-specific fields.
 */
export const WORLD_SPECIES_FIELD_DEFINITIONS: readonly WorldElementFieldDefinition[] = [
  ...SHARED_WORLD_ELEMENT_FIELDS,
  ...SPECIES_SPECIFIC_FIELDS,
] as const;

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Element subtypes for world species.
 * - creature: Animals, monsters, beasts (Dragons, Trolls, Dire Wolves)
 * - race: Sentient peoples, humanoid races (Elves, Dwarves, Androids)
 * - flora: Plants, fungi, vegetation (Corpse Flowers, Bloodvines, The World Tree)
 */
export const WorldSpeciesElementEnum = z.enum(['creature', 'race', 'flora']);
export type WorldSpeciesElement = z.infer<typeof WorldSpeciesElementEnum>;

// =============================================================================
// DYNAMICALLY GENERATED SCHEMAS (following character domain pattern)
// =============================================================================

/**
 * Builds the base world species attributes schema from the canonical field definitions.
 * All fields are optional for world species.
 */
const buildAttributesSchema = () => {
  const schemaShape = WORLD_SPECIES_FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = z.string().optional().describe(field.schemaDescription);
    return acc;
  }, {} as Record<string, z.ZodOptional<z.ZodString>>);

  // Type cast to preserve TypeScript inference (must be kept in sync with field definitions)
  return z.object(schemaShape as {
    // Shared fields
    relevanceToPlot: z.ZodOptional<z.ZodString>;
    currentState: z.ZodOptional<z.ZodString>;
    knownInformation: z.ZodOptional<z.ZodString>;
    // Species-specific fields
    biology: z.ZodOptional<z.ZodString>;
    habitat: z.ZodOptional<z.ZodString>;
    behavior: z.ZodOptional<z.ZodString>;
    diet: z.ZodOptional<z.ZodString>;
    intelligence: z.ZodOptional<z.ZodString>;
    society: z.ZodOptional<z.ZodString>;
    abilities: z.ZodOptional<z.ZodString>;
    population: z.ZodOptional<z.ZodString>;
  });
};

/**
 * The base schema for world species attributes, used by agents that generate or extract
 * species details. All attributes are optional.
 */
export const WorldSpeciesAttributesV3Schema = buildAttributesSchema();

// =============================================================================
// ENTITY SCHEMAS
// =============================================================================

/**
 * A point-in-time snapshot of a world species' state at the end of a specific chapter.
 * Extends attributes with temporal metadata.
 */
export const WorldSpeciesSnapshotV3Schema = WorldSpeciesAttributesV3Schema.extend({
  id: z.string().describe("Unique identifier for this snapshot (typically matches chapterId)."),
  chapterId: z.string().describe("ID of the chapter this snapshot corresponds to."),
  chapterNumber: z.number().describe("The number of the chapter."),
  timestamp: z.string().describe("ISO timestamp of when this snapshot was created."),
  notPresentInChapter: z.boolean().optional().describe("Flag indicating this is a carry-forward snapshot."),
});

/**
 * The canonical World Species entity.
 * A container for metadata and a timeline of snapshots.
 */
export const WorldSpeciesV3Schema = z.object({
  id: z.string().describe("Unique identifier for the world species."),
  name: z.string().describe("The name of the species."),
  type: z.literal('species').describe("Discriminator for world element type."),
  element: WorldSpeciesElementEnum.describe("The subtype of species (creature, race, or flora)."),
  initialDescription: z.string().optional().describe("Brief description of the species before detailed generation."),
  snapshots: z.array(WorldSpeciesSnapshotV3Schema).describe("Complete timeline of species state across chapters."),
  latestSnapshotId: z.string().optional().describe("Quick reference to the most recent snapshot."),
  notes: z.string().optional().describe("User notes about this species."),
  mainImageId: z.string().optional().describe("Reference to the main image for this species."),
});

// =============================================================================
// IDEATION SCHEMAS
// =============================================================================

/**
 * Schema for a world species idea before it's added to the project.
 * Used during AI ideation and extraction flows.
 */
export const WorldSpeciesIdeaV3Schema = z.object({
  tempId: z.string().describe("Temporary ID for UI tracking before persistence."),
  name: z.string().describe("The proposed name of the species."),
  element: WorldSpeciesElementEnum.describe("The proposed subtype (creature, race, or flora)."),
  initialDescription: z.string().describe("Brief description of the species concept."),
  source: z.enum(['ideation', 'extraction', 'manual']).describe("How this idea was generated."),
});

// =============================================================================
// CONTEXT SCHEMAS (for AI agents)
// =============================================================================

/**
 * Minimal reference schema for world species.
 * Used when only ID and name are needed.
 */
export const WorldSpeciesRefV3Schema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('species'),
});

/**
 * Basic context schema for world species.
 * Used for high-level summaries in agent context.
 */
export const WorldSpeciesBasicV3Schema = z.object({
  name: z.string(),
  type: z.literal('species'),
  element: WorldSpeciesElementEnum,
  initialDescription: z.string().optional(),
});

/**
 * Rich context schema for world species.
 * Includes the full entity plus the resolved latest snapshot.
 */
export const WorldSpeciesContextV3Schema = WorldSpeciesV3Schema.extend({
  latestSnapshot: WorldSpeciesSnapshotV3Schema.optional(),
});

// =============================================================================
// TYPE INFERENCE (single source of truth from Zod schemas)
// =============================================================================

export type WorldSpeciesAttributes = z.infer<typeof WorldSpeciesAttributesV3Schema>;
export type WorldSpeciesSnapshotV3 = z.infer<typeof WorldSpeciesSnapshotV3Schema>;
export type WorldSpeciesV3 = z.infer<typeof WorldSpeciesV3Schema>;
export type WorldSpeciesIdeaV3 = z.infer<typeof WorldSpeciesIdeaV3Schema>;
export type WorldSpeciesRefV3 = z.infer<typeof WorldSpeciesRefV3Schema>;
export type WorldSpeciesBasicV3 = z.infer<typeof WorldSpeciesBasicV3Schema>;
export type WorldSpeciesContextV3 = z.infer<typeof WorldSpeciesContextV3Schema>;
